import { Add } from "@mui/icons-material";
import {
  Box,
  BoxProps,
  Divider,
  Skeleton,
  styled,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useHistory, useParams } from "react-router-dom";

import { SparklesIcon } from "@/Components/Icons/SparklesIcons";
import { ButtonBasic } from "@/Components/v2/atoms/Button/ButtonBasic";
import { BetaPill } from "@/Components/v2/atoms/Pill";
import { DRAWER_BREAKPOINT, DRAWER_COLLAPSED_WIDTH_PX } from "@/Utils/GlobalStyle";

import { useListChats } from "./api/chats/chats.api";
import { ModelSelect } from "./components/ModelSelect";
import { useChat } from "./hooks/useChat";

const PreviousChatTextMaxChars = 65;
const PreviousChatsButton = styled(ButtonBasic)({
  height: "56px",
  textAlign: "left",
  justifyContent: "start",
  overflow: "hidden",
  lineHeight: "16px",
});

const NewChatButton = styled(ButtonBasic)(({ theme }) => ({
  borderRadius: "100px",
  padding: theme.spacing(1.5, 2.5),
  fontWeight: 600,
  lineHeight: "17px",
}));

export const ChatHistoryNavbar: React.FC<BoxProps> = (props) => {
  const { data: chats, isLoading } = useListChats();
  const { clearChat } = useChat();
  const history = useHistory();
  const isLargerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up(DRAWER_BREAKPOINT));

  const { chatId: currentChatId } = useParams<{ chatId?: string }>();
  const theme = useTheme();

  const handleStartNewChat = () => {
    clearChat();
  };

  const handlePreviousChatClick = (id: string) => () => {
    history.push(`/maite/${id}`);
  };

  return (
    <Box
      maxHeight="100vh"
      minWidth="300px"
      maxWidth="300px"
      p={1.5}
      ml={isLargerScreen ? 0 : `${DRAWER_COLLAPSED_WIDTH_PX}px`}
      zIndex={2}
      display="flex"
      flexDirection="column"
      alignItems="start"
      {...props}
    >
      <Box display="flex" alignItems="center">
        <SparklesIcon
          sx={{
            mr: 1.5,
          }}
          fill={theme.palette.primary.contrastText}
        />
        <Typography variant="h2" py={1.5}>
          MAITE
        </Typography>
        <BetaPill />
      </Box>
      <ModelSelect />
      <Divider
        sx={{
          my: 1.5,
          mr: 2.5,
          alignSelf: "stretch",
        }}
      />

      <NewChatButton
        onClick={handleStartNewChat}
        variant="contained"
        color="primary"
        disabled={isLoading || !currentChatId}
        startIcon={<Add />}
      >
        New Chat
      </NewChatButton>

      <Typography variant="h3" my={1.5}>
        Past Chats:
      </Typography>

      {isLoading ? (
        <>
          <Skeleton
            variant="rounded"
            height="56px"
            width="100%"
            sx={{ borderRadius: 3, mb: 0.5 }}
          />
          <Skeleton
            variant="rounded"
            height="56px"
            width="100%"
            sx={{ borderRadius: 3, mb: 0.5 }}
          />
        </>
      ) : (
        <Box
          flex={1}
          sx={{
            overflowY: "auto",
          }}
        >
          {chats?.data.map((chat) => (
            <PreviousChatsButton
              key={chat.id}
              onClick={handlePreviousChatClick(chat.id)}
              fullWidth
              sx={{
                mb: 0.5,
              }}
              color={chat.id === currentChatId ? "active" : "secondary"}
              variant="contained"
            >
              {chat.initialQuery.length > PreviousChatTextMaxChars
                ? `${chat.initialQuery.slice(0, PreviousChatTextMaxChars)}...`
                : chat.initialQuery}
            </PreviousChatsButton>
          ))}
        </Box>
      )}
    </Box>
  );
};
